import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { usePersistedState, useStableKeyId } from '@/hooks/usePersistedState';
import axios from 'axios';
import {
  Package, Plus, Search, Trash2, Edit2, X, DollarSign,
  Users, Clock, AlertTriangle, CheckCircle, Settings, Filter,
  Download, Upload, FileSpreadsheet, Info, ChevronDown, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, subDays } from 'date-fns';
import * as XLSX from 'xlsx';
import CommissionBoardModal from './CommissionBoardModal';
import { SALES_ADMINS } from '../constants/salesAdmins';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  'Pending', 'Done', 'Recheck TV', 'Remove Access', 'Removed',
  'Done Indi Only (Recheck Email)', 'Done Indi Only (Balance Not Enough)', 'Expired'
];
const CLIENT_STATUS_OPTIONS = ['New Client', 'Existing Client'];
const ADMIN_LIST = SALES_ADMINS;

const STATUS_COLORS = {
  'Pending': 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  'Done': 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  'Recheck TV': 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  'Remove Access': 'bg-orange-500/15 text-orange-400 border-orange-500/30',
  'Removed': 'bg-red-500/15 text-red-400 border-red-500/30',
  'Done Indi Only (Recheck Email)': 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  'Done Indi Only (Balance Not Enough)': 'bg-pink-500/15 text-pink-400 border-pink-500/30',
  'Expired': 'bg-gray-500/15 text-gray-400 border-gray-500/30',
};

// Section wrapper matching CRM style
function SectionCard({ children, className = '' }) {
  return <div className={`rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] overflow-hidden ${className}`}>{children}</div>;
}

function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#E63946]/20 via-[#F97316]/15 to-transparent relative overflow-hidden rounded-t-xl">
      <div className="absolute inset-0 bg-gradient-to-r from-[#E63946]/10 to-transparent opacity-50" />
      <div className="relative flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20 border border-[#E63946]/40 shadow-[0_0_15px_-3px_#E63946]">
          <Icon size={18} className="text-[#E63946]" />
        </div>
        <div>
          <h2 className="text-sm font-bold text-white tracking-tight">{title}</h2>
          {subtitle && <p className="text-[11px] text-[hsl(var(--text-muted))]">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
}

export default function ProductInstallationContent({ ib }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [commissionSummary, setCommissionSummary] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const P = `product-install:${useStableKeyId(ib?.id)}`;
  const [searchQuery, setSearchQuery] = usePersistedState(`${P}:search`, '');
  const [statusFilter, setStatusFilter] = usePersistedState(`${P}:status`, 'all');
  const [productFilter, setProductFilter] = usePersistedState(`${P}:product`, 'all');
  const [dateFilter, setDateFilter] = usePersistedState(`${P}:date`, 'all');
  const [selectedMonth, setSelectedMonth] = usePersistedState(`${P}:month`, '');
  const [newProductName, setNewProductName] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [editingRemarks, setEditingRemarks] = useState(null);
  const [editingBroker, setEditingBroker] = useState(null);
  const [editingCountry, setEditingCountry] = useState(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [commissionMonth, setCommissionMonth] = useState('');
  const [currentPage, setCurrentPage] = usePersistedState(`${P}:page`, 1);
  const [pageSize, setPageSize] = usePersistedState(`${P}:pageSize`, 15);
  const [syncingCT, setSyncingCT] = useState(false);

  const emptyForm = {
    client_name: '', email: '', phone: '', account_id: '', username_tv: '',
    deposit: '', product: '', client_status: 'New Client',
    status: 'Pending', closed_by: '', remarks: '', broker: '', country: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    date_expiry: format(new Date(new Date().setDate(new Date().getDate() + 30)), 'yyyy-MM-dd'),
  };
  const [form, setForm] = useState(emptyForm);

  const getToken = () => localStorage.getItem('token');

  const fetchRecords = useCallback(async () => {
    if (!ib?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/product-installations/${ib.id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setRecords(res.data.records || []);
    } catch (err) { console.error('Fetch error:', err); }
    finally { setLoading(false); }
  }, [ib?.id]);

  const fetchProducts = useCallback(async () => {
    if (!ib?.id) return;
    try {
      const res = await axios.get(`${API}/api/product-installations/${ib.id}/products`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setProducts(res.data.products || []);
    } catch (_e) { /* silent */ }
  }, [ib?.id]);

  const fetchCommission = useCallback(async (month) => {
    if (!ib?.id) return;
    try {
      const params = month ? `?month=${month}` : '';
      const res = await axios.get(`${API}/api/product-installations/${ib.id}/commission-summary${params}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setCommissionSummary(res.data.summary || []);
    } catch (_e) { /* silent */ }
  }, [ib?.id]);

  useEffect(() => { fetchRecords(); fetchProducts(); fetchCommission(); }, [fetchRecords, fetchProducts, fetchCommission]);

  const handleSyncToClientTracker = useCallback(async () => {
    if (!ib?.id) return;
    if (!window.confirm(`Sync semua ${records.length} record Product Installation ke Client Tracker?\n\nRecord yang sudah wujud akan di-merge (fields kosong sahaja diisi).`)) return;
    setSyncingCT(true);
    try {
      const res = await axios.post(
        `${API}/api/product-installations/${ib.id}/sync-to-client-tracker`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      const d = res.data || {};
      toast.success(
        `Sync selesai: ${d.created} baru, ${d.merged} di-merge, ${d.skipped} skip (${d.total_records} total)`,
        { duration: 5000 }
      );
    } catch (err) {
      toast.error('Sync gagal: ' + (err.response?.data?.detail || err.message || ''));
    } finally {
      setSyncingCT(false);
    }
  }, [ib?.id, records.length]);

  // Distinct suggestions for broker & country (autocomplete from existing records)
  const brokerSuggestions = useMemo(
    () => Array.from(new Set(records.map(r => (r.broker || '').trim()).filter(Boolean))).sort(),
    [records]
  );
  const countrySuggestions = useMemo(
    () => Array.from(new Set(records.map(r => (r.country || '').trim()).filter(Boolean))).sort(),
    [records]
  );

  // Distinct client-name suggestions for the Add Client modal — sourced ONLY
  // from records of the CURRENT channel (per user requirement: keep autofill
  // scoped per-channel, don't mix cross-channel clients). Keeps the most
  // recent record per unique client_name.
  const clientRecordByName = useMemo(() => {
    const map = {};
    // Iterate records sorted by date desc so the newest record wins per name
    const sorted = [...records].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    for (const r of sorted) {
      const key = (r.client_name || '').trim().toLowerCase();
      if (!key) continue;
      if (!map[key]) map[key] = r;
    }
    return map;
  }, [records]);
  const clientNameSuggestions = useMemo(
    () => Object.values(clientRecordByName).map(r => (r.client_name || '').trim()).sort((a, b) => a.localeCompare(b)),
    [clientRecordByName]
  );

  // When user types a name that matches an existing client exactly, prefill
  // the rest of the form so admin only needs to update deposit/product/date.
  const handleClientNameChange = (val) => {
    const nextForm = { ...form, client_name: val };
    // Only autofill when NOT editing an existing record (to avoid overwriting
    // in-progress edits) and when the typed value exactly matches a known name.
    if (!editingRecord) {
      const match = clientRecordByName[val.trim().toLowerCase()];
      if (match) {
        nextForm.email = match.email || nextForm.email;
        nextForm.phone = match.phone || nextForm.phone;
        nextForm.account_id = match.account_id || nextForm.account_id;
        nextForm.username_tv = match.username_tv || nextForm.username_tv;
        nextForm.broker = match.broker || nextForm.broker;
        nextForm.country = match.country || nextForm.country;
        nextForm.product = match.product || nextForm.product;
        // Repeat-client heuristic: same person coming back = Existing Client
        nextForm.client_status = 'Existing Client';
        toast.success(`Autofilled from previous record (${match.date || 'no date'})`);
      }
    }
    setForm(nextForm);
  };

  // Generate month options (always show last 12 months)
  const monthOptions = useMemo(() => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(format(d, 'yyyy-MM'));
    }
    return months;
  }, []);

  // Filtered records
  const filteredRecords = useMemo(() => {
    let f = [...records];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      f = f.filter(r => [r.client_name, r.email, r.phone, r.account_id, r.username_tv, r.remarks, r.broker, r.country].some(v => (v || '').toLowerCase().includes(q)));
    }
    if (statusFilter !== 'all') f = f.filter(r => r.status === statusFilter);
    if (productFilter !== 'all') f = f.filter(r => r.product === productFilter);
    if (selectedMonth) f = f.filter(r => (r.date || '').startsWith(selectedMonth));
    else if (dateFilter !== 'all') {
      const now = new Date();
      const bounds = dateFilter === 'today' ? { s: format(now, 'yyyy-MM-dd'), e: format(now, 'yyyy-MM-dd') }
        : dateFilter === 'week' ? { s: format(startOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd'), e: format(endOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd') }
        : dateFilter === 'month' ? { s: format(startOfMonth(now), 'yyyy-MM-dd'), e: format(endOfMonth(now), 'yyyy-MM-dd') }
        : dateFilter === 'last7' ? { s: format(subDays(now, 7), 'yyyy-MM-dd'), e: format(now, 'yyyy-MM-dd') } : null;
      if (bounds) f = f.filter(r => r.date >= bounds.s && r.date <= bounds.e);
    }
    return f;
  }, [records, searchQuery, statusFilter, productFilter, dateFilter, selectedMonth]);

  // Pagination
  const totalPages = Math.ceil(filteredRecords.length / pageSize);
  const paginatedRecords = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRecords.slice(start, start + pageSize);
  }, [filteredRecords, currentPage, pageSize]);

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [searchQuery, statusFilter, productFilter, dateFilter, selectedMonth]);

  // Scores
  const scores = useMemo(() => ({
    total: filteredRecords.length,
    pending: filteredRecords.filter(r => r.status === 'Pending').length,
    done: filteredRecords.filter(r => r.status === 'Done').length,
    newClients: filteredRecords.filter(r => r.client_status === 'New Client').length,
    expired: filteredRecords.filter(r => r.status === 'Expired').length,
    totalDeposit: filteredRecords.reduce((s, r) => s + (parseFloat(r.deposit) || 0), 0),
  }), [filteredRecords]);

  // CRUD
  const handleSubmit = async () => {
    if (!form.client_name.trim()) { toast.error('Client name required'); return; }
    try {
      const payload = { ...form, deposit: parseFloat(form.deposit) || 0 };
      if (!payload.closed_by) delete payload.closed_by;
      const headers = { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' };
      if (editingRecord) {
        await axios.put(`${API}/api/product-installations/${ib.id}/${editingRecord.id}`, payload, { headers });
        toast.success('Record updated');
      } else {
        await axios.post(`${API}/api/product-installations/${ib.id}`, payload, { headers });
        toast.success('Client added');
      }
      setShowAddModal(false); setEditingRecord(null); setForm(emptyForm);
      fetchRecords(); fetchCommission();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      await axios.delete(`${API}/api/product-installations/${ib.id}/${id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success('Deleted'); fetchRecords(); fetchCommission();
    } catch { toast.error('Delete failed'); }
  };

  const handleStatusChange = async (record, newStatus) => {
    try {
      await axios.put(`${API}/api/product-installations/${ib.id}/${record.id}`, { status: newStatus }, { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' } });
      fetchRecords(); fetchCommission();
    } catch { toast.error('Update failed'); }
  };

  // Bulk status change
  const handleBulkStatusChange = async (newStatus) => {
    if (!selectedIds.length) return;
    try {
      const headers = { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' };
      await Promise.all(selectedIds.map(id =>
        axios.put(`${API}/api/product-installations/${ib.id}/${id}`, { status: newStatus }, { headers })
      ));
      toast.success(`${selectedIds.length} records updated to ${newStatus}`);
      setSelectedIds([]);
      fetchRecords(); fetchCommission();
    } catch { toast.error('Bulk update failed'); }
  };

  // Inline remarks save
  const saveRemarks = async (record, newRemarks) => {
    try {
      await axios.put(`${API}/api/product-installations/${ib.id}/${record.id}`, { remarks: newRemarks }, { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' } });
      setEditingRemarks(null);
      fetchRecords();
    } catch { toast.error('Save failed'); }
  };

  // Inline broker/country save
  const saveField = async (record, field, value) => {
    try {
      await axios.put(`${API}/api/product-installations/${ib.id}/${record.id}`, { [field]: value }, { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' } });
      if (field === 'broker') setEditingBroker(null);
      if (field === 'country') setEditingCountry(null);
      fetchRecords();
    } catch { toast.error('Save failed'); }
  };

  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleSelectAll = () => setSelectedIds(prev => prev.length === filteredRecords.length ? [] : filteredRecords.map(r => r.id));

  const openEdit = (record) => {
    setForm({ client_name: record.client_name || '', email: record.email || '', phone: record.phone || '', account_id: record.account_id || '', username_tv: record.username_tv || '', deposit: record.deposit?.toString() || '', product: record.product || '', client_status: record.client_status || 'New Client', status: record.status || 'Pending', closed_by: record.closed_by || '', remarks: record.remarks || '', broker: record.broker || '', country: record.country || '', date: record.date || format(new Date(), 'yyyy-MM-dd'), date_expiry: record.date_expiry || '' });
    setEditingRecord(record); setShowAddModal(true);
  };

  // Product management
  const addProduct = async () => {
    if (!newProductName.trim()) return;
    const updated = [...products, newProductName.trim()];
    try {
      await axios.post(`${API}/api/product-installations/${ib.id}/products`, { products: updated }, { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' } });
      setProducts(updated); setNewProductName(''); toast.success('Product added');
    } catch { toast.error('Failed'); }
  };

  const removeProduct = async (name) => {
    const updated = products.filter(p => p !== name);
    try {
      await axios.post(`${API}/api/product-installations/${ib.id}/products`, { products: updated }, { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' } });
      setProducts(updated); toast.success('Removed');
    } catch { toast.error('Failed'); }
  };

  // ===== IMPORT / EXPORT =====
  const TEMPLATE_HEADERS = ['Client Name', 'Email', 'Phone', 'Account ID', 'Username TV', 'Deposit', 'Product', 'Broker', 'Country', 'Client Status', 'Status', 'Closed By', 'Remarks', 'Date'];
  const HEADER_MAP = { 'client name': 'client_name', 'client_name': 'client_name', 'email': 'email', 'phone': 'phone', 'phone number': 'phone', 'no telefon': 'phone', 'account id': 'account_id', 'account_id': 'account_id', 'moneta id': 'account_id', 'username tv': 'username_tv', 'username_tv': 'username_tv', 'tradingview': 'username_tv', 'deposit': 'deposit', 'product': 'product', 'broker': 'broker', 'country': 'country', 'client status': 'client_status', 'client_status': 'client_status', 'status': 'status', 'closed by': 'closed_by', 'closed_by': 'closed_by', 'remarks': 'remarks', 'date': 'date' };

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS, ['John Doe', 'john@email.com', '+60123456789', '12345', 'john_tv', '100', products[0] || 'Product Name', 'Exness', 'Malaysia', 'New Client', 'Pending', 'Syakir', '', format(new Date(), 'yyyy-MM-dd')]]);
    ws['!cols'] = TEMPLATE_HEADERS.map(() => ({ wch: 20 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, `Product_Installation_Template_${ib?.name || 'IB'}.xlsx`);
    toast.success('Template downloaded');
  };

  const exportData = (type) => {
    const rows = filteredRecords.map(r => [r.client_name, r.email, r.phone, r.account_id, r.username_tv, r.deposit, r.product, r.broker, r.country, r.client_status, r.status, r.closed_by, r.remarks, r.date]);
    const ws = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS, ...rows]);
    ws['!cols'] = TEMPLATE_HEADERS.map(() => ({ wch: 20 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Records');
    const ext = type === 'csv' ? 'csv' : 'xlsx';
    XLSX.writeFile(wb, `Product_Installation_${ib?.name}_${format(new Date(), 'yyyyMMdd')}.${ext}`, { bookType: ext === 'csv' ? 'csv' : 'xlsx' });
    toast.success(`Exported ${filteredRecords.length} records`);
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: '' });
      if (!json.length) { toast.error('File is empty'); return; }

      let imported = 0;
      const headers = Object.keys(json[0]);
      const mapping = {};
      headers.forEach(h => { const k = HEADER_MAP[h.toLowerCase().trim()]; if (k) mapping[h] = k; });

      for (const row of json) {
        const record = { ...emptyForm };
        Object.entries(mapping).forEach(([from, to]) => { if (row[from] !== undefined) record[to] = String(row[from]); });
        record.deposit = parseFloat(record.deposit) || 0;
        if (!record.client_name) return;
        if (!record.date || record.date === '') record.date = format(new Date(), 'yyyy-MM-dd');
        try {
          await axios.post(`${API}/api/product-installations/${ib.id}`, record, { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' } });
          imported++;
        } catch (_e) { /* skip failed row */ }
      }
      toast.success(`Imported ${imported} of ${json.length} records`);
      fetchRecords(); fetchCommission();
      setShowImportModal(false);
    } catch (err) { toast.error('Import failed: ' + (err.message || '')); }
    e.target.value = '';
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20 gap-2">
      <Clock size={18} className="animate-spin text-[#E63946]" />
      <span className="text-[13px] text-[hsl(var(--text-muted))]">Loading records...</span>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="product-installation">
      {/* Sticky Header - CRM standard */}
      <header className="md:sticky md:top-0 z-20 bg-[#09090B]/95 backdrop-blur-md border-b border-[hsl(var(--border))]">
        <div className="px-4 sm:px-8 py-3 sm:py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h1 className="text-lg sm:text-2xl font-bold font-heading tracking-tight">Product Installation</h1>
            <p className="text-xs sm:text-sm text-[hsl(var(--text-muted))]">{ib?.name} — Client Records & Tracking</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" className="text-xs h-8" onClick={() => setShowCommissionModal(true)} data-testid="commission-btn">
              <DollarSign size={13} className="mr-1 text-[#10b981]" /> Commission
            </Button>
            <Button size="sm" variant="outline" className="text-xs h-8" onClick={() => setShowProductModal(true)} data-testid="manage-products-btn">
              <Settings size={13} className="mr-1" /> Products
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className="text-xs h-8"><Download size={13} className="mr-1" /> Export <ChevronDown size={10} className="ml-1" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => exportData('xlsx')}><FileSpreadsheet size={13} className="mr-2 text-[#3B82F6]" />Excel (.xlsx)</DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportData('csv')}><FileSpreadsheet size={13} className="mr-2 text-[#10B981]" />CSV</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button size="sm" variant="outline" className="text-xs h-8" onClick={() => setShowImportModal(true)}>
              <Upload size={13} className="mr-1" /> Import
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-8 border-[#10b981]/40 text-[#10b981] hover:bg-[#10b981]/10 hover:text-[#10b981]"
              onClick={handleSyncToClientTracker}
              disabled={syncingCT || records.length === 0}
              data-testid="sync-to-client-tracker-btn"
              title="Duplicate semua record ke Client Tracker (merge kalau dah wujud). Auto-sync jalan untuk record baru."
            >
              <RefreshCw size={13} className={`mr-1 ${syncingCT ? 'animate-spin' : ''}`} />
              {syncingCT ? 'Syncing…' : 'Sync ke Client Tracker'}
            </Button>
            <Button size="sm" className="text-xs h-8 bg-[#E63946] hover:bg-[#c62828]" onClick={() => { setForm(emptyForm); setEditingRecord(null); setShowAddModal(true); }} data-testid="add-client-btn">
              <Plus size={13} className="mr-1" /> Add Client
            </Button>
          </div>
        </div>
      </header>

      <div className="px-4 sm:px-8 space-y-5">
        {/* Score Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { label: 'Total Records', value: scores.total, icon: Users, color: '#3b82f6' },
            { label: 'Pending', value: scores.pending, icon: Clock, color: '#f59e0b' },
            { label: 'Done', value: scores.done, icon: CheckCircle, color: '#10b981' },
            { label: 'New Client', value: scores.newClients, icon: Plus, color: '#8b5cf6' },
            { label: 'Expired', value: scores.expired, icon: AlertTriangle, color: '#ef4444' },
            { label: 'Total Deposit', value: `$${scores.totalDeposit.toLocaleString()}`, icon: DollarSign, color: '#10b981' },
          ].map(card => (
            <div key={card.label} className="p-3.5 rounded-xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] hover:border-[hsl(var(--border-hover))] transition-all group" data-testid={`score-${card.label.toLowerCase().replace(/\s/g, '-')}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center transition-all" style={{ background: `${card.color}15` }}>
                  <card.icon size={13} style={{ color: card.color }} />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))]">{card.label}</span>
              </div>
              <p className="text-xl font-black font-mono" style={{ color: card.color }}>{card.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <SectionCard>
          <div className="px-4 py-3 flex flex-wrap items-center gap-2.5">
            <div className="relative flex-1 min-w-[180px] max-w-[280px]">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
              <Input placeholder="Search client, email, ID..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 h-8 text-xs bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]" data-testid="search-input" />
            </div>
            <div className="flex items-center gap-1">
              {['all', 'today', 'week', 'month', 'last7'].map(f => (
                <button key={f} onClick={() => { setDateFilter(f); setSelectedMonth(''); }}
                  className={`px-2.5 py-1.5 rounded-lg text-[10px] font-semibold transition-all ${dateFilter === f && !selectedMonth ? 'bg-[#E63946] text-white shadow-lg shadow-[#E63946]/20' : 'bg-[hsl(var(--surface-elevated))] text-[hsl(var(--text-muted))] border border-[hsl(var(--border))] hover:text-white'}`}
                  data-testid={`date-filter-${f}`}>
                  {f === 'all' ? 'All' : f === 'today' ? 'Today' : f === 'week' ? 'Week' : f === 'month' ? 'Month' : '7d'}
                </button>
              ))}
            </div>
            {monthOptions.length > 0 && (
              <Select value={selectedMonth || '__all__'} onValueChange={v => { setSelectedMonth(v === '__all__' ? '' : v); if (v !== '__all__') setDateFilter('all'); }}>
                <SelectTrigger className="w-[140px] h-8 text-[10px] bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]" data-testid="month-filter">
                  <SelectValue placeholder="Select Month" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Months</SelectItem>
                  {monthOptions.map(m => <SelectItem key={m} value={m}>{format(new Date(m + '-01'), 'MMM yyyy')}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[130px] h-8 text-[10px] bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]" data-testid="status-filter">
                <Filter size={11} className="mr-1" /><SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            {products.length > 0 && (
              <Select value={productFilter} onValueChange={setProductFilter}>
                <SelectTrigger className="w-[130px] h-8 text-[10px] bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]" data-testid="product-filter">
                  <Package size={11} className="mr-1" /><SelectValue placeholder="Product" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Products</SelectItem>
                  {products.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        </SectionCard>

        {/* Bulk Actions Bar */}
        {selectedIds.length > 0 && (
          <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-[#E63946]/10 border border-[#E63946]/30">
            <span className="text-xs font-semibold text-white">{selectedIds.length} selected</span>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-[hsl(var(--text-muted))]">Change status:</span>
              {['Done', 'Pending', 'Expired', 'Removed'].map(s => (
                <button key={s} onClick={() => handleBulkStatusChange(s)} className={`px-2.5 py-1 rounded text-[10px] font-semibold border transition-all hover:scale-105 ${STATUS_COLORS[s]}`}>{s}</button>
              ))}
            </div>
            <button onClick={() => { setSelectedIds([]); setBulkMode(false); }} className="ml-auto text-[10px] text-[hsl(var(--text-muted))] hover:text-white">Cancel</button>
          </div>
        )}

        {/* Records Table */}
        <SectionCard>
          <div className="px-6 py-3 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#E63946]/20 via-[#F97316]/15 to-transparent flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20 border border-[#E63946]/40 shadow-[0_0_15px_-3px_#E63946]">
                <Package size={18} className="text-[#E63946]" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-white tracking-tight">Installation Records</h2>
                <p className="text-[11px] text-[hsl(var(--text-muted))]">{filteredRecords.length} of {records.length} records</p>
              </div>
            </div>
            <Button size="sm" variant={bulkMode ? 'default' : 'outline'} className="text-[10px] h-7" onClick={() => { setBulkMode(!bulkMode); if (bulkMode) setSelectedIds([]); }} data-testid="bulk-select-btn">
              {bulkMode ? 'Cancel Select' : 'Select Multiple'}
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[hsl(var(--surface-elevated))] border-b border-[hsl(var(--border))]">
                  {bulkMode && (
                    <th className="py-3 px-3 w-10 text-center">
                      <input type="checkbox" checked={selectedIds.length === filteredRecords.length && filteredRecords.length > 0} onChange={toggleSelectAll} className="rounded border-gray-600 bg-transparent cursor-pointer" />
                    </th>
                  )}
                  <th className="py-3 px-4 text-center text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))]">Client</th>
                  <th className="py-3 px-4 text-center text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))]">Account ID</th>
                  <th className="py-3 px-4 text-center text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))]">Username TV</th>
                  <th className="py-3 px-4 text-center text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))]">Deposit</th>
                  <th className="py-3 px-4 text-center text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))]">Product</th>
                  <th className="py-3 px-4 text-center text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))]">Broker</th>
                  <th className="py-3 px-4 text-center text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))]">Country</th>
                  <th className="py-3 px-4 text-center text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))]">Status</th>
                  <th className="py-3 px-4 text-center text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))]">Closed By</th>
                  <th className="py-3 px-4 text-center text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))]">Date Added</th>
                  <th className="py-3 px-4 text-center text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))]">Date Expiry</th>
                  <th className="py-3 px-4 text-center text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))]">Remarks</th>
                  <th className="py-3 px-3 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.length === 0 ? (
                  <tr><td colSpan={bulkMode ? 14 : 13} className="py-16 text-center">
                    <Package size={32} className="mx-auto mb-2 text-[hsl(var(--text-muted))]/30" />
                    <p className="text-sm text-[hsl(var(--text-muted))]">No records found</p>
                    <p className="text-xs text-[hsl(var(--text-muted))]/60 mt-1">Add your first client or import from a spreadsheet</p>
                  </td></tr>
                ) : paginatedRecords.map(r => (
                  <tr key={r.id} className={`border-b border-[hsl(var(--border))]/20 hover:bg-[hsl(var(--surface-elevated))]/50 transition-colors group ${selectedIds.includes(r.id) ? 'bg-[#E63946]/5' : ''}`}>
                    {bulkMode && (
                      <td className="py-3 px-3 text-center">
                        <input type="checkbox" checked={selectedIds.includes(r.id)} onChange={() => toggleSelect(r.id)} className="rounded border-gray-600 bg-transparent cursor-pointer" />
                      </td>
                    )}
                    <td className="py-3 px-4 text-center">
                      <p className="font-semibold text-white text-[13px]">{r.client_name}</p>
                      <p className="text-[11px] text-[hsl(var(--text-muted))]">{r.email}</p>
                      {r.phone && <p className="text-[10px] text-[hsl(var(--text-muted))]/70">{r.phone}</p>}
                      <Badge variant="outline" className={`text-[9px] mt-1 ${r.client_status === 'New Client' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' : 'bg-blue-500/10 text-blue-400 border-blue-500/30'}`}>{r.client_status}</Badge>
                    </td>
                    <td className="py-3 px-4 text-center text-[13px] font-mono text-[#3b82f6]">{r.account_id}</td>
                    <td className="py-3 px-4 text-center text-[13px] text-[#10b981]">{r.username_tv}</td>
                    <td className="py-3 px-4 text-center text-[13px] font-mono font-bold text-emerald-400">${r.deposit || 0}</td>
                    <td className="py-3 px-4 text-center">{r.product && <Badge variant="outline" className="text-[10px] bg-[#8b5cf6]/10 text-[#a78bfa] border-[#8b5cf6]/30">{r.product}</Badge>}</td>
                    <td className="py-3 px-4 text-center max-w-[130px]">
                      {editingBroker === r.id ? (
                        <>
                          <input list={`broker-list-${r.id}`} autoFocus defaultValue={r.broker || ''}
                            onBlur={e => saveField(r, 'broker', e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') saveField(r, 'broker', e.target.value); if (e.key === 'Escape') setEditingBroker(null); }}
                            className="w-full h-7 px-2 text-[11px] bg-[#111118] border border-[#3b82f6]/50 rounded text-white outline-none text-center"
                            data-testid={`broker-input-${r.id}`} />
                          <datalist id={`broker-list-${r.id}`}>
                            {brokerSuggestions.map(b => <option key={b} value={b} />)}
                          </datalist>
                        </>
                      ) : (
                        <p onClick={() => setEditingBroker(r.id)} className="text-[11px] cursor-pointer hover:text-white transition-colors" title={r.broker || 'Click to add'}
                          data-testid={`broker-cell-${r.id}`}>
                          {r.broker ? <Badge variant="outline" className="text-[10px] bg-cyan-500/10 text-cyan-400 border-cyan-500/30">{r.broker}</Badge> : <span className="italic opacity-40 text-[hsl(var(--text-muted))]">+ add</span>}
                        </p>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center max-w-[130px]">
                      {editingCountry === r.id ? (
                        <>
                          <input list={`country-list-${r.id}`} autoFocus defaultValue={r.country || ''}
                            onBlur={e => saveField(r, 'country', e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') saveField(r, 'country', e.target.value); if (e.key === 'Escape') setEditingCountry(null); }}
                            className="w-full h-7 px-2 text-[11px] bg-[#111118] border border-[#3b82f6]/50 rounded text-white outline-none text-center"
                            data-testid={`country-input-${r.id}`} />
                          <datalist id={`country-list-${r.id}`}>
                            {countrySuggestions.map(c => <option key={c} value={c} />)}
                          </datalist>
                        </>
                      ) : (
                        <p onClick={() => setEditingCountry(r.id)} className="text-[11px] cursor-pointer hover:text-white transition-colors" title={r.country || 'Click to add'}
                          data-testid={`country-cell-${r.id}`}>
                          {r.country ? <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-400 border-amber-500/30">{r.country}</Badge> : <span className="italic opacity-40 text-[hsl(var(--text-muted))]">+ add</span>}
                        </p>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <select value={r.status} onChange={e => handleStatusChange(r, e.target.value)}
                        className={`text-[10px] font-semibold px-2 py-1.5 rounded-md border cursor-pointer bg-transparent outline-none max-w-[120px] ${STATUS_COLORS[r.status] || 'text-white border-gray-600'}`}
                        data-testid={`status-select-${r.id}`}>
                        {STATUS_OPTIONS.map(s => <option key={s} value={s} className="bg-[#1a1a25] text-white">{s}</option>)}
                      </select>
                    </td>
                    <td className="py-3 px-4 text-center text-[13px] text-[hsl(var(--text-muted))]">{r.closed_by || '—'}</td>
                    <td className="py-3 px-4 text-center text-[12px] text-[hsl(var(--text-muted))] font-mono">{r.date}</td>
                    <td className="py-3 px-4 text-center text-[12px] font-mono" data-testid={`date-expiry-${r.id}`}>
                      {(() => {
                        const exp = r.date_expiry;
                        if (!exp) return <span className="text-[hsl(var(--text-muted))]">—</span>;
                        const days = Math.floor((new Date(exp) - new Date(format(new Date(), 'yyyy-MM-dd'))) / (1000 * 60 * 60 * 24));
                        const cls = days < 0 ? 'text-[#EF4444] font-bold'
                          : days <= 3 ? 'text-[#F59E0B] font-bold'
                          : days <= 7 ? 'text-[#10B981]'
                          : 'text-white';
                        return <span className={cls}>{exp}{days >= 0 && days <= 7 && <span className="ml-1 text-[9px]">({days}d)</span>}</span>;
                      })()}
                    </td>
                    <td className="py-3 px-4 text-center max-w-[150px]">
                      {editingRemarks === r.id ? (
                        <input autoFocus defaultValue={r.remarks || ''} onBlur={e => saveRemarks(r, e.target.value)} onKeyDown={e => { if (e.key === 'Enter') saveRemarks(r, e.target.value); if (e.key === 'Escape') setEditingRemarks(null); }}
                          className="w-full h-7 px-2 text-[11px] bg-[#111118] border border-[#3b82f6]/50 rounded text-white outline-none text-center" />
                      ) : (
                        <p onClick={() => setEditingRemarks(r.id)} className="text-[11px] text-[hsl(var(--text-muted))] truncate cursor-pointer hover:text-white transition-colors" title={r.remarks || 'Click to add'}>
                          {r.remarks || <span className="italic opacity-40">+ add</span>}
                        </p>
                      )}
                    </td>
                    <td className="py-3 px-3 text-center">
                      <div className="flex items-center justify-center gap-0.5 opacity-40 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => openEdit(r)} className="p-1.5 rounded-md hover:bg-[hsl(var(--surface-elevated))] text-[hsl(var(--text-muted))] hover:text-white transition-all" data-testid={`edit-${r.id}`}><Edit2 size={13} /></button>
                        <button onClick={() => handleDelete(r.id)} className="p-1.5 rounded-md hover:bg-red-500/10 text-[hsl(var(--text-muted))] hover:text-red-400 transition-all" data-testid={`delete-${r.id}`}><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {filteredRecords.length > 0 && (
            <div className="px-4 py-3 border-t border-[hsl(var(--border))] flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-[hsl(var(--text-muted))]">Show</span>
                <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                  className="h-7 px-2 text-[11px] bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded text-white outline-none cursor-pointer">
                  {[10, 15, 20, 30, 50].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <span className="text-[11px] text-[hsl(var(--text-muted))]">per page</span>
                <span className="text-[11px] text-[hsl(var(--text-muted))] ml-2">|</span>
                <span className="text-[11px] text-white ml-2">{(currentPage-1)*pageSize+1}–{Math.min(currentPage*pageSize, filteredRecords.length)} of {filteredRecords.length}</span>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}
                  className="px-2 py-1 rounded text-[10px] border border-[hsl(var(--border))] text-[hsl(var(--text-muted))] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed">First</button>
                <button onClick={() => setCurrentPage(p => Math.max(1, p-1))} disabled={currentPage === 1}
                  className="px-2 py-1 rounded text-[10px] border border-[hsl(var(--border))] text-[hsl(var(--text-muted))] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed">Prev</button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let page;
                  if (totalPages <= 5) page = i + 1;
                  else if (currentPage <= 3) page = i + 1;
                  else if (currentPage >= totalPages - 2) page = totalPages - 4 + i;
                  else page = currentPage - 2 + i;
                  return (
                    <button key={page} onClick={() => setCurrentPage(page)}
                      className={`w-7 h-7 rounded text-[11px] font-semibold transition-all ${currentPage === page ? 'bg-[#E63946] text-white' : 'border border-[hsl(var(--border))] text-[hsl(var(--text-muted))] hover:text-white'}`}>{page}</button>
                  );
                })}
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p+1))} disabled={currentPage === totalPages}
                  className="px-2 py-1 rounded text-[10px] border border-[hsl(var(--border))] text-[hsl(var(--text-muted))] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed">Next</button>
                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}
                  className="px-2 py-1 rounded text-[10px] border border-[hsl(var(--border))] text-[hsl(var(--text-muted))] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed">Last</button>
              </div>
            </div>
          )}
        </SectionCard>
      </div>

      {/* ========== ADD/EDIT MODAL ========== */}
      <Dialog open={showAddModal} onOpenChange={v => {
        if (!v) { setShowAddModal(false); setEditingRecord(null); }
        else {
          setShowAddModal(true);
          // Refresh records when modal opens so autofill has the latest data
          // (fixes: some users saw stale/empty suggestions after other users
          // added new clients in the same channel).
          fetchRecords();
        }
      }}>
        <DialogContent className="sm:max-w-lg bg-[#0a0a0f] border-[#1a1a25] text-white max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-sm font-bold">{editingRecord ? 'Edit Client' : 'Add New Client'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            <div className="sm:col-span-2">
              <label className="text-[9px] font-bold uppercase text-[hsl(var(--text-muted))] mb-1 block">Client Name * <span className="text-[8px] text-[hsl(var(--text-muted))] font-normal normal-case">— type existing client to autofill</span></label>
              <Input list="form-client-name-suggestions" value={form.client_name} onChange={e => handleClientNameChange(e.target.value)} placeholder="Full name" className="h-9 text-xs bg-[#111118] border-[#1a1a25]" data-testid="form-client-name" />
              <datalist id="form-client-name-suggestions">
                {clientNameSuggestions.map(n => <option key={n} value={n} />)}
              </datalist>
            </div>
            <div>
              <label className="text-[9px] font-bold uppercase text-[hsl(var(--text-muted))] mb-1 block">Email</label>
              <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="email@example.com" className="h-9 text-xs bg-[#111118] border-[#1a1a25]" data-testid="form-email" />
            </div>
            <div>
              <label className="text-[9px] font-bold uppercase text-[hsl(var(--text-muted))] mb-1 block">Phone</label>
              <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+60123456789" className="h-9 text-xs bg-[#111118] border-[#1a1a25]" data-testid="form-phone" />
            </div>
            <div>
              <label className="text-[9px] font-bold uppercase text-[hsl(var(--text-muted))] mb-1 block">Account ID</label>
              <Input value={form.account_id} onChange={e => setForm({ ...form, account_id: e.target.value })} placeholder="Broker account ID" className="h-9 text-xs bg-[#111118] border-[#1a1a25]" data-testid="form-account-id" />
            </div>
            <div>
              <label className="text-[9px] font-bold uppercase text-[hsl(var(--text-muted))] mb-1 block">Username TV</label>
              <Input value={form.username_tv} onChange={e => setForm({ ...form, username_tv: e.target.value })} placeholder="TradingView username" className="h-9 text-xs bg-[#111118] border-[#1a1a25]" data-testid="form-username-tv" />
            </div>
            <div>
              <label className="text-[9px] font-bold uppercase text-[hsl(var(--text-muted))] mb-1 block">Deposit ($)</label>
              <Input type="number" value={form.deposit} onChange={e => setForm({ ...form, deposit: e.target.value })} placeholder="0" className="h-9 text-xs bg-[#111118] border-[#1a1a25]" data-testid="form-deposit" />
            </div>
            <div>
              <label className="text-[9px] font-bold uppercase text-[hsl(var(--text-muted))] mb-1 block">Product</label>
              <select value={form.product} onChange={e => setForm({ ...form, product: e.target.value })} className="w-full h-9 text-xs bg-[#111118] border border-[#1a1a25] rounded-md px-3 text-white outline-none" data-testid="form-product">
                <option value="">Select product</option>
                {products.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[9px] font-bold uppercase text-[hsl(var(--text-muted))] mb-1 block">Broker</label>
              <Input list="form-broker-suggestions" value={form.broker} onChange={e => setForm({ ...form, broker: e.target.value })} placeholder="e.g. Exness" className="h-9 text-xs bg-[#111118] border-[#1a1a25]" data-testid="form-broker" />
              <datalist id="form-broker-suggestions">
                {brokerSuggestions.map(b => <option key={b} value={b} />)}
              </datalist>
            </div>
            <div>
              <label className="text-[9px] font-bold uppercase text-[hsl(var(--text-muted))] mb-1 block">Country</label>
              <Input list="form-country-suggestions" value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} placeholder="e.g. Malaysia" className="h-9 text-xs bg-[#111118] border-[#1a1a25]" data-testid="form-country" />
              <datalist id="form-country-suggestions">
                {countrySuggestions.map(c => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div>
              <label className="text-[9px] font-bold uppercase text-[hsl(var(--text-muted))] mb-1 block">Client Status</label>
              <select value={form.client_status} onChange={e => setForm({ ...form, client_status: e.target.value })} className="w-full h-9 text-xs bg-[#111118] border border-[#1a1a25] rounded-md px-3 text-white outline-none" data-testid="form-client-status">
                {CLIENT_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[9px] font-bold uppercase text-[hsl(var(--text-muted))] mb-1 block">Status</label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full h-9 text-xs bg-[#111118] border border-[#1a1a25] rounded-md px-3 text-white outline-none" data-testid="form-status">
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[9px] font-bold uppercase text-[hsl(var(--text-muted))] mb-1 block">Closed By</label>
              <select value={form.closed_by} onChange={e => setForm({ ...form, closed_by: e.target.value })} className="w-full h-9 text-xs bg-[#111118] border border-[#1a1a25] rounded-md px-3 text-white outline-none" data-testid="form-closed-by">
                <option value="">— None —</option>
                {ADMIN_LIST.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[9px] font-bold uppercase text-[hsl(var(--text-muted))] mb-1 block">Date Added</label>
              <Input
                type="date"
                value={form.date}
                onChange={e => {
                  const newDate = e.target.value;
                  // Live-sync expiry to date+30d whenever user changes Date Added.
                  // Users can still manually override expiry after — this only
                  // fires when date changes, not when expiry itself is edited.
                  let newExpiry = form.date_expiry;
                  if (newDate) {
                    try {
                      const d = new Date(newDate);
                      d.setDate(d.getDate() + 30);
                      newExpiry = format(d, 'yyyy-MM-dd');
                    } catch { /* keep existing */ }
                  }
                  setForm({ ...form, date: newDate, date_expiry: newExpiry });
                }}
                className="h-9 text-xs bg-[#111118] border-[#1a1a25]"
                data-testid="form-date"
              />
            </div>
            <div>
              <label className="text-[9px] font-bold uppercase text-[hsl(var(--text-muted))] mb-1 block">Date Expiry <span className="text-[8px] text-[hsl(var(--text-muted))] font-normal normal-case">— auto: date added + 30d</span></label>
              <Input type="date" value={form.date_expiry} onChange={e => setForm({ ...form, date_expiry: e.target.value })} className="h-9 text-xs bg-[#111118] border-[#1a1a25]" data-testid="form-date-expiry" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[9px] font-bold uppercase text-[hsl(var(--text-muted))] mb-1 block">Remarks</label>
              <Input value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} placeholder="Optional notes..." className="h-9 text-xs bg-[#111118] border-[#1a1a25]" data-testid="form-remarks" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" size="sm" className="text-xs" onClick={() => { setShowAddModal(false); setEditingRecord(null); }}>Cancel</Button>
            <Button size="sm" className="text-xs bg-[#E63946] hover:bg-[#c62828]" onClick={handleSubmit} data-testid="form-submit">{editingRecord ? 'Update' : 'Add Client'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ========== PRODUCT MANAGEMENT MODAL ========== */}
      <Dialog open={showProductModal} onOpenChange={setShowProductModal}>
        <DialogContent className="sm:max-w-md bg-[#0a0a0f] border-[#1a1a25] text-white">
          <DialogHeader><DialogTitle className="text-sm font-bold flex items-center gap-2"><Package size={15} className="text-[#8b5cf6]" /> Products — {ib?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="flex gap-2">
              <Input value={newProductName} onChange={e => setNewProductName(e.target.value)} placeholder="New product name..." className="h-9 text-xs bg-[#111118] border-[#1a1a25]" data-testid="new-product-input" onKeyDown={e => e.key === 'Enter' && addProduct()} />
              <Button size="sm" className="h-9 bg-[#E63946] hover:bg-[#c62828]" onClick={addProduct} data-testid="add-product-btn"><Plus size={13} /></Button>
            </div>
            <div className="space-y-1.5">
              {products.length === 0 && <p className="text-[11px] text-[hsl(var(--text-muted))] py-6 text-center">No products yet</p>}
              {products.map(p => (
                <div key={p} className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-[#111118] border border-[#1a1a25] hover:border-[#2a2a35] transition-all">
                  <span className="text-[12px] text-white flex items-center gap-2"><Package size={12} className="text-[#8b5cf6]" />{p}</span>
                  <button onClick={() => removeProduct(p)} className="text-[hsl(var(--text-muted))] hover:text-red-400 transition-colors"><X size={13} /></button>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ========== COMMISSION MODAL ========== */}
      <CommissionBoardModal open={showCommissionModal} onOpenChange={setShowCommissionModal} ibId={ib.id} ibName={ib.name} />

      {/* ========== IMPORT MODAL ========== */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="sm:max-w-lg bg-[#0a0a0f] border-[#1a1a25] text-white">
          <DialogHeader><DialogTitle className="text-sm font-bold flex items-center gap-2"><Upload size={15} className="text-[#3b82f6]" /> Import Data</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Tutorial */}
            <div className="p-3 rounded-lg bg-[#3b82f6]/5 border border-[#3b82f6]/20">
              <div className="flex items-start gap-2">
                <Info size={14} className="text-[#3b82f6] mt-0.5 shrink-0" />
                <div className="text-[11px] text-[#93c5fd] space-y-1">
                  <p className="font-semibold text-[#3b82f6]">Cara Import:</p>
                  <p>1. Download template Excel di bawah</p>
                  <p>2. Isi data client mengikut column headers</p>
                  <p>3. Upload file (.xlsx, .xls, .csv)</p>
                  <p className="text-[10px] text-[hsl(var(--text-muted))] mt-1">Headers yang dikenali: Client Name, Email, Phone, Account ID, Username TV, Deposit, Product, Broker, Country, Client Status, Status, Closed By, Remarks, Date</p>
                </div>
              </div>
            </div>
            <Button variant="outline" size="sm" className="w-full text-xs" onClick={downloadTemplate}>
              <Download size={13} className="mr-2" /> Download Template (.xlsx)
            </Button>
            <div className="border-2 border-dashed border-[#1a1a25] rounded-xl p-6 text-center hover:border-[#3b82f6]/40 transition-all">
              <Upload size={24} className="mx-auto mb-2 text-[hsl(var(--text-muted))]" />
              <p className="text-[11px] text-[hsl(var(--text-muted))] mb-2">Drop file or click to upload</p>
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleImport} className="absolute inset-0 opacity-0 cursor-pointer" style={{ position: 'relative' }} data-testid="import-file-input" />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
